/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : Information of selected Lead
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

//Style
import style from "../../Style/Component/lead.module.scss";

//Custom components
import { CustomTab, EmptyScreen, Loader, OverLay } from '../../Components/Others';
import { Icon } from '../../Components/Icon';
import { ChangeStage, NextFollowDate, ActivityHistory } from './Helper';
import { APIService } from '../../Service';
import { CustomDataTable } from '../../Components/DataTable';
import { PrimaryButton, TextButton } from '../../Components/Buttons';
import { AddNoteModal, ConfirmModal } from '../../Components/Modal';

const TAB_DATA = [
    { id: 1, label: 'Notes' },
    { id: 2, label: 'Change stage' },
    { id: 3, label: 'Set next follow-up date ' },
    { id: 4, label: 'Activity history' }
];

const ITEMS_PER_PAGE = 20;

const LeadDetail = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const { id } = useParams();
    const isRendered = useRef(true);

    const [toggleMenu, setToggleMenu] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const [loader, setLoader] = useState(true);
    const [detailData, setDetailData] = useState({});

    const [currentPage, setCurrentPage] = useState(1);

    const [addNoteModal, setAddNoteModal] = useState(false);
    const [edit, setEdit] = useState(false);
    const [editNote, setEditNote] = useState({});

    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState(false);

    const [leadStageList, setLeadStageList] = useState([]);

    const [shortName, setShortName] = useState("");

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            setSelectedTab(state.tab_index);
        } else {
            setSelectedTab(1);
        }
    }, [state]);

    useEffect(() => {
        if (addNoteModal === true) {
            const add_modal = new Modal(document.getElementById('add-note-modal'));
            add_modal.show();
        }
    }, [addNoteModal]);

    useEffect(() => {
        if (confirmDeleteModal === true) {
            const modal = new Modal(document.getElementById('confirm-modal'));
            modal.show();
        }
    }, [confirmDeleteModal]);

    useEffect(() => {
        if (isRendered.current === true) {
            _leadDetail();
            _leadStageList();
            isRendered.current = false;
        }
    }, []);

    // handle toggle activate or deactivate 
    const _handleToggleMenu = () => {
        setToggleMenu(prev => !prev);
    };

    // handle tab change
    const _handleTab = (tab_id) => {
        navigate(`/leads/${id}`, {
            state: {
                tab_index: tab_id
            }
        });
    };

    // handle delete lead 
    const _handleDelete = () => {
        setToggleMenu(false);
        setConfirmDeleteModal(true);
    };

    // handle add note 
    const _handleAddNote = () => {
        setAddNoteModal(true);
    };

    // handle edit note 
    const _handleEdit = (data) => {
        setAddNoteModal(true);
        setEditNote(data);
        setEdit(true);
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setCurrentPage(page_num);
    };

    // Leads data table column 
    const TABLE_COLUMN = [
        {
            name: 'Added by',
            selector: row => row.added_by,
            width: "200px"
        },
        {
            name: 'Date & time',
            selector: row => row.created,
            width: "240px"
        },
        {
            name: 'Note content',
            selector: row => row.note,
            width: "390px"
        },
        {
            name: '',
            cell: (row) =>
                <TextButton name="Edit"
                    onClick={() => _handleEdit(row)} />,
            width: "120px"
        },
    ];

    // API - Get lead details
    const _leadDetail = () => {
        let is_post = true;
        let url = 'lead/details';
        let data = {
            "lead_id": id,
            "page_num": 1,
            "page_size": ITEMS_PER_PAGE
        };

        setLoader(true);
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                let updated_name = response.data.lead_details.name.split(" ");
                let short_name;

                if (updated_name.length > 1) {
                    short_name = updated_name[0].substring(0, 1) + updated_name[1].substring(0, 1)
                } else {
                    short_name = updated_name[0].substring(0, 2)
                }

                setShortName(short_name.toUpperCase());

                setDetailData(response.data);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
                navigate("/leads");
                setDetailData({ notes: {} });
            }
            setLoader(false);
        });
    };

    // API - Delete lead
    const _deleteLead = () => {
        let is_post = true;
        let url = 'lead/delete-lead';
        let data = {
            "lead_id": id
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDeleteStatus(true);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        });
    };

    // API - Get lead stage list
    const _leadStageList = () => {
        let is_post = false;
        let url = 'lead/list-lead-stage';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                let updated_data = response.data.map(data => ({
                    value: data.id,
                    label: data.name
                }));

                setLeadStageList(updated_data);
            } else {
                setLeadStageList([]);
            }
        });
    };


    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                {loader ?
                    <Loader className="h-100 align-items-center justify-content-center" />
                    :
                    <>
                        <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                            <p className='mb-0 e-text-chinese-black e-primary-font e-font-16 e-font-weight-400 line-height-24px'>
                                <span className='text-decoration-hover-underline cursor-pointer'
                                    onClick={() => navigate("/leads")}>Leads</span> / <span className='e-font-weight-600'>{detailData.lead_details.name}</span>
                            </p>
                        </div>

                        <div className='padding-52px-lr margin--80px-top'>
                            {/* Lead summary */}
                            <div className={`${style.e_summary_wrapper} e-bg-american-violet border-radius-16 padding-32px-tb px-4 position-relative`}>
                                <div className='d-flex gap-4 align-items-start'>
                                    <div className='e-bg-blue-violet-light border-radius-64 p-4'>
                                        <p className='mb-0 e-text-cultured-light e-primary-font e-font-20 e-font-weight-400 line-height-20px'>
                                            {shortName}
                                        </p>
                                    </div>

                                    {/* Basic detail of admin  */}
                                    <div className='d-flex align-items-center gap-3'>
                                        <div className='d-flex flex-column gap-12px'>
                                            <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-600 line-height-18px'>
                                                {detailData.lead_details.name}
                                            </p>
                                            <a href={`mailto:${detailData.lead_details.email}`} target='_blank' className='mb-0 d-flex e-text-platinum e-primary-font e-font-14 e-font-weight-600 line-height-18px text-decoration-none text-decoration-hover-underline w-max'>
                                                {detailData.lead_details.email}
                                            </a>
                                            <a href={`tel:${detailData.lead_details.mobile}`} className='d-flex e-text-platinum e-primary-font e-font-14 e-font-weight-600 line-height-18px text-decoration-none text-decoration-hover-underline w-max'>
                                                {detailData.lead_details.mobile}
                                            </a>
                                        </div>
                                        <div className='vr e-bg-lavender-blue'></div>
                                        <div className='d-flex flex-column gap-12px'>
                                            <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                                RM code: <span className='e-font-weight-600'>{detailData.lead_details.rm_code}</span>
                                            </p>
                                            <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                                Reporting to: <span className='e-font-weight-600'> {detailData.lead_details.assigned_to}</span>
                                            </p>
                                            <p className='mb-0 e-text-platinum e-primary-font e-font-14 e-font-weight-400 line-height-18px'>
                                                Added on: <span className='e-font-weight-600'> {detailData.lead_details.created}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Toggle menu  */}
                                <>
                                    <Icon icon="menu"
                                        size="1.5rem"
                                        height="1.375rem"
                                        color="#FFFFFF"
                                        className="position-absolute right-32px top-16px cursor-pointer"
                                        onClick={_handleToggleMenu} />

                                    {toggleMenu &&
                                        <>
                                            <OverLay isOpen={toggleMenu} onClose={_handleToggleMenu} />
                                            <div className={`${style.e_menu_popup} e-bg-white border-radius-8 position-absolute right-32px top-48px padding-10px-tb padding-12px-lr d-flex gap-1 align-items-center z-index-9 cursor-pointer`}
                                                onClick={_handleDelete} >
                                                <img src={require("../../Assets/Image/Others/delete.svg").default}
                                                    draggable={false}
                                                    alt='delete' />

                                                <p className='mb-0 e-text-raisin-black e-primary-font e-font-12 e-font-weight-600 line-height-16px'>
                                                    Delete Lead
                                                </p>
                                            </div>
                                        </>
                                    }
                                </>

                                <div className={`d-flex gap-12px align-items-center e-bg-lavender-dark border-radius-4 p-3 mt-4 w-max-content`}>
                                    <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                        <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                            Last contacted date
                                        </p>
                                        <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                            {detailData.lead_details.last_contacted_date}
                                        </p>
                                    </div>

                                    <div className='vr e-bg-vodka-light'></div>
                                    <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                        <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                            Next follow up date
                                        </p>
                                        <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                            {detailData.lead_details.next_follow_up_date}
                                        </p>
                                    </div>

                                    <div className='vr e-bg-vodka-light'></div>

                                    <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                        <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                            Lead stage
                                        </p>
                                        <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                            {detailData.lead_details.lead_stage_name}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Custom tab */}
                            <div className='d-flex py-4 mt-2'>
                                <CustomTab data={TAB_DATA}
                                    selectedTab={selectedTab}
                                    tooltip={false}
                                    handleClick={_handleTab} />
                            </div>

                            <div className={`${style.e_data_wrapper} e-bg-ghost-white border-radius-16`}>
                                {/* Note list  */}
                                {selectedTab === 1 ?
                                    // <Notes notesData={detailData.notes} />
                                    <div className='padding-32px-tb '>
                                        <div className='padding-32px-lr d-flex justify-content-between align-items-center'>
                                            <h4 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                                Note list
                                            </h4>
                                            <PrimaryButton name="Add Note"
                                                paddingBlock="padding-12px-tb padding-42px-lr"
                                                onClick={_handleAddNote} />
                                        </div>

                                        {/* Note list table */}
                                        {Object.keys(detailData.notes).length !== 0 && detailData.notes.data.length !== 0 ?
                                            <div className='e-data-table-wrapper-default-updated pt-2 mt-1'>
                                                <CustomDataTable columns={TABLE_COLUMN}
                                                    data={detailData.notes.data}
                                                    lastPage={detailData.total_page}
                                                    pagination={true}
                                                    paginationPerPage={ITEMS_PER_PAGE}
                                                    paginationTotalRows={detailData.notes.total_count}
                                                    paginationDefaultPage={detailData.notes.current_page}
                                                    onChangePage={_handleTablePageChange} />
                                            </div>
                                            :
                                            <EmptyScreen width={120}
                                                height={120}
                                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                                title='No data found'
                                            />
                                        }
                                    </div>
                                    :
                                    // change lead stage 
                                    selectedTab === 2 ?
                                        <ChangeStage data={detailData.lead_details}
                                            leadStage={leadStageList}
                                            handleUpdate={() => _leadDetail()} />
                                        :
                                        // set next follow up 
                                        selectedTab === 3 ?
                                            <NextFollowDate data={detailData.lead_details}
                                                handleUpdate={() => _leadDetail()} />
                                            :
                                            // Activity log
                                            <ActivityHistory activityData={detailData.activity_log} />
                                }
                            </div>
                        </div>
                    </>
                }
            </section>

            {/* add note modal  */}
            {addNoteModal === true &&
                <AddNoteModal edit={edit}
                    id={id}
                    editData={editNote}
                    handleClose={() => {
                        setAddNoteModal(false);
                        setEdit(false);
                    }}
                    handleSuccess={() => {
                        setAddNoteModal(false);
                        setEdit(false);
                        setEditNote({})
                        _leadDetail();
                    }} />
            }

            {confirmDeleteModal === true &&
                <ConfirmModal title={deleteStatus ? "Deleted successfully" : "Confirm Delete"}
                    description="Lorem Ipsum is simply dummy text of the printing and typesetting industry."
                    status={deleteStatus}
                    handleConfirm={_deleteLead}
                    handleClose={() => {
                        setTimeout(() => {
                            setConfirmDeleteModal(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setConfirmDeleteModal(false);
                            navigate("/leads");
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default LeadDetail;
